import {createAsyncThunk, createSlice, isAnyOf, PayloadAction} from '@reduxjs/toolkit';
import {IErrorResult, IServerError,} from '../../interface/Basic/api';

import {
    auth,
    authToken,
    bindConnection,
    checkConnections, checkConnectionsAdmin,
    confirmRecoveryCode,
    confirmUser,
    continueConnection, continueConnectionAdmin,
    getConfirmationRequirements, getPerson,
    getPhone,
    getRegistrationInitData,
    logout,
    passportCheck,
    recoveryEmail,
    recoverySendCode,
    refreshToken,
    repeatCode, replace,
    retryMailCode,
    retrySMS,
    searchUser,
    setPassword,
    signIn,
    skipConnection,
    submitMailCode,
    submitSMS,
    updatePassword,
    verifyMail,
    verifyPhone
} from "./api";
import {
    AuthAdmin,
    AuthToken,
    IAccessTokenResponse, IAuthAdminProps,
    IAuthError,
    IAuthProps,
    IAuthResponse,
    IAuthState,
    IAuthTokenProps,
    IBindConnectionProps,
    IGetMailCodeProps, IGetPersonProps,
    ILoginResponse,
    IMailSubmitProps,
    IPasswordSubmit,
    IPhoneSubmitProps,
    IRefreshTokenProps,
    IRegisterProps, IReplace,
    IResponse,
    IResponseWithRetryData,
    IRetryPhoneCodeProps,
    ISendPhoneCodeProps
} from "../../interface/Auth/frontend";
import {RecoveryStepEnum, RegisterStepEnum} from "../../enum/auth";
import {resetRegister} from "../../utils/resetRegister";
import {APIStatusEnum} from "../../enum/api";
import {
    ConfirmationRequirementsModel, ICheckConnectionsAdminResponse,
    ICheckConnectionsResponse,
    IConfirmRecoveryCodeResponse,
    IConfirmationRequirementsResult,
    IConfirmRecoveryCodeRequest,
    IConnectionResponse,
    IErrorResultWithRetryData,
    IGetRecoveryPhoneResponse,
    IPersons,
    IRecoverySendCodeEmail,
    IRecoverySendCodeResponse,
    IRegisterFormResponse,
    IRegistrationInitResult,
    IRepeatRecoveryCodeResponse,
    ISearchUserResponse,
    IServerErrorWithRetryData,
    IUpdatePasswordRequest,
    RecoverySearchUser,
    RecoverySendCodeData,
    RegistrationInitDataModel
} from "../../interface/Auth/backend";
import moment from "moment";
import {LocalStorageEnum} from "../../enum/localStorage";

export const SendPhoneCode = createAsyncThunk<IAccessTokenResponse, ISendPhoneCodeProps, {
    rejectValue: IErrorResultWithRetryData
}>
('sendPhoneCode', (data, thunkAPI) => verifyPhone(data)
    .then(resolve => resolve)
    .catch((error: IServerErrorWithRetryData) => {
        if (error.response.data.error.status === APIStatusEnum.TooManyRequests) {
            error.response.data.error.retryData = error.response.headers['retry-after'];
        }
        return thunkAPI.rejectWithValue(error.response.data.error)
    }))

export const SubmitPhoneCode = createAsyncThunk<IResponse, IPhoneSubmitProps, { rejectValue: IErrorResult }>
('submitSMS', (data, thunkAPI) => submitSMS(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const RetrySMS = createAsyncThunk<IResponseWithRetryData, IRetryPhoneCodeProps, {
    rejectValue: IErrorResultWithRetryData
}>
('retrySMS', (data, thunkAPI) => retrySMS(data)
    .then(resolve => resolve)
    .catch((error: IServerErrorWithRetryData) => {
        if (error.response.data.error.status === APIStatusEnum.TooManyRequests) {
            error.response.data.error.retryData = error.response.headers['retry-after'];
        }
        return thunkAPI.rejectWithValue(error.response.data.error)
    }))

export const SendMailCode = createAsyncThunk<IAccessTokenResponse, IGetMailCodeProps, {
    rejectValue: IErrorResultWithRetryData
}>
('sendMailCode', (data, thunkAPI) => verifyMail(data)
    .then(resolve => resolve)
    .catch((error: IServerErrorWithRetryData) => {
        if (error.response.data.error.status === APIStatusEnum.TooManyRequests) {
            error.response.data.error.retryData = error.response.headers['retry-after'];
        }
        return thunkAPI.rejectWithValue(error.response.data.error)
    }))

export const SubmitMailCode = createAsyncThunk<IResponse, IMailSubmitProps, { rejectValue: IErrorResult }>
('submitMailCode', (data, thunkAPI) => submitMailCode(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))


export const RetryMailCode = createAsyncThunk<IResponseWithRetryData, IGetMailCodeProps, {
    rejectValue: IErrorResultWithRetryData
}>
('retryMailCode', (data, thunkAPI) => retryMailCode(data)
    .then(resolve => resolve)
    .catch((error: IServerErrorWithRetryData) => {
        if (error.response.data.error.status === APIStatusEnum.TooManyRequests) {
            error.response.data.error.retryData = error.response.headers['retry-after'];
        }
        return thunkAPI.rejectWithValue(error.response.data.error)
    }))

export const SetPassword = createAsyncThunk<IAccessTokenResponse, IPasswordSubmit, { rejectValue: IErrorResult }>
('setPassword', (data, thunkAPI) => setPassword(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const SignIn = createAsyncThunk<ILoginResponse, AuthToken, { rejectValue: IErrorResult }>
('signIn', (data, thunkAPI) => signIn(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const RefreshToken = createAsyncThunk<ILoginResponse, IRefreshTokenProps, { rejectValue: IErrorResult }>
('refreshToken', (data, thunkAPI) => refreshToken(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

//OAUTH2
export const Authorization = createAsyncThunk<IAuthResponse, AuthToken, { rejectValue: IErrorResult }>
('authorization', (data, thunkAPI) => auth(data)
    .then(resolve => resolve)
    .catch((error: IAuthError) => thunkAPI.rejectWithValue(error.response?.data.error)))

//OAUTH2
export const AuthorizationByToken = createAsyncThunk<IAuthResponse, IAuthTokenProps, { rejectValue: IErrorResult }>
('AuthorizationByToken', (data, thunkAPI) => authToken(data)
    .then(resolve => resolve)
    .catch((error: IAuthError) => thunkAPI.rejectWithValue(error.response?.data.error)))

export const Logout = createAsyncThunk<undefined, undefined, { rejectValue: IErrorResult | null }>
('logout', (data, thunkAPI) => logout()
    .then(resolve => resolve)
    .catch((error: IAuthError) => thunkAPI.rejectWithValue(error.response?.data.error)))


export const SearchUser = createAsyncThunk<ISearchUserResponse, string, { rejectValue: IErrorResult }>
('searchUser', (data, thunkAPI) => searchUser(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const RecoverySendCode = createAsyncThunk<IRecoverySendCodeResponse, string, { rejectValue: IErrorResult }>
('recoverySendCode', (data, thunkAPI) => recoverySendCode(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const RecoveryByEmail = createAsyncThunk<IRecoverySendCodeEmail, string, { rejectValue: IErrorResult }>
('recoveryEmail', (data, thunkAPI) => recoveryEmail(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const ConfirmUser = createAsyncThunk<undefined, string, { rejectValue: IErrorResult }>
('confirmUser', (data, thunkAPI) => confirmUser(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const RepeatRecoveryCode = createAsyncThunk<IRepeatRecoveryCodeResponse, {
    token: string,
    sentTo: string|null
}, {
    rejectValue: IErrorResultWithRetryData
}>
('RepeatRecoveryCode', (data, thunkAPI) => repeatCode(data.token, data.sentTo)
    .then(resolve => resolve)
    .catch((error: IServerErrorWithRetryData) => {
        if (error.response.data.error.status === APIStatusEnum.TooManyRequests) {
            error.response.data.error.retryData = error.response.headers['retry-after'];
        }
        return thunkAPI.rejectWithValue(error.response.data.error)
    }))

export const ConfirmRecoveryCode = createAsyncThunk<IConfirmRecoveryCodeResponse, IConfirmRecoveryCodeRequest, {
    rejectValue: IErrorResult
}>
('confirmRecoveryCode', (data, thunkAPI) => confirmRecoveryCode(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const UpdatePassword = createAsyncThunk<undefined, IUpdatePasswordRequest, { rejectValue: IErrorResult }>
('updatePassword', (data, thunkAPI) => updatePassword(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const GetPhone = createAsyncThunk<IGetRecoveryPhoneResponse, string, { rejectValue: IErrorResult }>
('getPhone', (data, thunkAPI) => getPhone(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const PassportCheck = createAsyncThunk<IRegisterFormResponse, IRegisterProps, { rejectValue: IErrorResult }>
('passportCheck', (data, thunkAPI) => passportCheck(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const CheckConnections = createAsyncThunk<ICheckConnectionsResponse, IAuthProps, { rejectValue: IErrorResult }>
('CheckConnections', (data, thunkAPI) => checkConnections(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

// Вход админа под пользователем
export const CheckConnectionsAdmin = createAsyncThunk<ICheckConnectionsAdminResponse, IAuthAdminProps, { rejectValue: IErrorResult,  }>
('CheckConnectionsAdmin', (data, thunkAPI) => checkConnectionsAdmin(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const SkipConnection = createAsyncThunk<IConnectionResponse, AuthToken, { rejectValue: IErrorResult }>
('SkipConnection', (data, thunkAPI) => skipConnection(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const ContinueConnection = createAsyncThunk<IConnectionResponse, AuthToken, { rejectValue: IErrorResult }>
('ContinueConnection', (data, thunkAPI) => continueConnection(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const ContinueConnectionAdmin = createAsyncThunk<IConnectionResponse, AuthAdmin, { rejectValue: IErrorResult }>
('ContinueConnectionAdmin', (data, thunkAPI) => continueConnectionAdmin(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const Replace = createAsyncThunk<IConnectionResponse, IReplace, { rejectValue: IErrorResult }>
('Replace', (data, thunkAPI) => replace(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const BindConnection = createAsyncThunk<IConnectionResponse, IBindConnectionProps, { rejectValue: IErrorResult }>
('BindConnection', (data, thunkAPI) => bindConnection(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const GetRegistrationInitData = createAsyncThunk<IRegistrationInitResult, string, { rejectValue: IErrorResult }>
('GetRegistrationInitData', (data, thunkAPI) => getRegistrationInitData(data)
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const GetConfirmationRequirements = createAsyncThunk<IConfirmationRequirementsResult, void, {
    rejectValue: IErrorResult
}>
('GetConfirmationRequirements', (data, thunkAPI) => getConfirmationRequirements()
    .then(resolve => resolve)
    .catch((error: IServerError) => thunkAPI.rejectWithValue(error.response.data.error)))

export const GetPersons = createAsyncThunk<IPersons, IGetPersonProps, { rejectValue: IErrorResult }>
('getPersons', (data) => getPerson(data).then(resolve => resolve))

export const GetPerson = createAsyncThunk<IPersons, IGetPersonProps, { rejectValue: IErrorResult }>
('getPerson', (data) => getPerson(data).then(resolve => resolve))

const InitialState: IAuthState = {
    recoveryStep: RecoveryStepEnum.Recovery,
    registerStep: RegisterStepEnum.Start,
    isFetching: false,
    error: {},
    phone: null,
    mail: null,
    phoneSubmit: false,
    mailSubmit: false,
    smsPending: false,
    codePending: false,
    retryAfter: null,
    callbackDelay: null,
    transaction_id: null,
    passwordFilled: false,
    recoveryData: null,
    recoveryUserInfo: null,
    recoveryPhone: null,
    recoveryEmail: null,
    captcha: localStorage.getItem('captcha'),
    recoveryPassword: null,
    // registered: false,
    isAuthFetching: false,
    token: localStorage.getItem('access_token'),
    isPassportChecking: false,
    connection: {
        accountSubmitted: false,
        isBinding: false,
        canLogIn: false,
        token: null,
        accountToConnectId: null,
        connectedAccountId: null,
        data: [],
    },
    registerInitData: null,
    registerInitDataFetching: false,
    needPhoneVerification: true,
    filledRegisterForm: null,
    recoveryType: null,
    sentTo: null,
    needPasswordReset: false,
    needAuth: null,
    loginAdminToken: null,
    loginAdminEmail: null,
    loginAdminInitialPerson: null,
    loginAdminPersons: [],
    loginAdminSuccess: false,
    isTryingAsAdmin: false,
    accounts: [],
    isFetchingInAuth:false,
    unbindingsAccounts: false
}

export const slice = createSlice({
    name: 'authSlice',
    initialState: InitialState,
    reducers: {
        setRecoveryStep(state, action: PayloadAction<RecoveryStepEnum>) {
            state.recoveryStep = action.payload
        },
        cleanError(state, action: PayloadAction<string>) {
            delete state.error[action.payload]
        },
        addError(state, action: PayloadAction<{ key: string, value: string }>) {
            state.error[action.payload.key] = action.payload.value
        },
        cleanAllErrors(state) {
            state.error = {}
        },
        setRegisterStep(state, action: PayloadAction<RegisterStepEnum>) {
            state.registerStep = action.payload
        },
        setPhone(state, action: PayloadAction<string | null>) {
            state.phone = action.payload
        },
        setMail(state, action: PayloadAction<string | null>) {
            state.mail = action.payload
        },
        setPhoneSubmit(state, action: PayloadAction<boolean>) {
            state.phoneSubmit = action.payload
        },
        setMailSubmit(state, action: PayloadAction<boolean>) {
            state.mailSubmit = action.payload
        },
        setRecoveryData(state, action: PayloadAction<string | null>) {
            state.recoveryData = action.payload
        },
        resetState(state) {
            state.registerStep = RegisterStepEnum.Start
            state.isFetching = false
            state.error = {}
            state.phone = null
            state.mail = null
            state.phoneSubmit = false
            state.mailSubmit = false
            state.retryAfter = null
            state.token = null
            state.needPasswordReset = false
        },
        setCaptcha(state, action: PayloadAction<string | null>) {
            state.captcha = action.payload
        },
        setRecoveryPassword(state, action: PayloadAction<string>) {
            state.recoveryPassword = action.payload
        },
        clearCountdown(state) {
            state.retryAfter = null
        },
        clearCallbackDelay(state) {
            state.callbackDelay = 0
        },
        setAccountToConnectId(state, action: PayloadAction<string | null>) {
            state.connection.accountToConnectId = action.payload
        },
        resetPasswordFilled(state) {
            state.passwordFilled = false
            state.passwordFilled = false
        },
        setNeedPasswordReset(state, action: PayloadAction<boolean>) {
            state.needPasswordReset = action.payload
        },
        setNeedAuth(state, action: PayloadAction<boolean | null>) {
            state.needAuth = action.payload
        },
        loginAdminExit(state) {
            state.loginAdminToken = null
        },
        setIsTryingAsAdmin(state, action: PayloadAction<boolean>) {
            state.isTryingAsAdmin = action.payload
        },
        forceSetToUserChoose(state){
            state.loginAdminToken = state.connection.token;
        },
        clearAndExit(state){
            state.connection.data = []
            state.connection.token = null
            state.connection.canLogIn = false
            state.accounts = []
            state.needAuth = null
            state.loginAdminEmail = null
        }
    },
    extraReducers: builder => {

        builder.addCase(RefreshToken.fulfilled, (state, action) => {
            localStorage.setItem(LocalStorageEnum.access_token, action.payload.access_token)
            localStorage.setItem(LocalStorageEnum.refresh_token_time, action.payload.expires_in.toString())
            localStorage.setItem(LocalStorageEnum.refresh_token, action.payload.refresh_token)
        })
        builder.addCase(SignIn.fulfilled, (state, action) => {
            localStorage.setItem(LocalStorageEnum.access_token, action.payload.access_token)
            localStorage.setItem(LocalStorageEnum.refresh_token_time, action.payload.expires_in.toString())
            localStorage.setItem(LocalStorageEnum.refresh_token, action.payload.refresh_token)
            localStorage.removeItem(LocalStorageEnum.registerToken)
            state.loginAdminToken = null
            state.loginAdminSuccess = false
            state.token = action.payload.access_token
            state.isAuthFetching = false
        })
        builder.addCase(SignIn.rejected, (state, action) => {
            state.isAuthFetching = false
            if (action.payload?.fields) {
                Object.entries(action.payload.fields).forEach((item) => {
                    const [key, values] = item
                    if (values && values.length !== 0) {
                        state.error[key] = values.join(', ')
                    }
                })
            }
        })
        builder.addCase(SignIn.pending, (state) => {
            state.isAuthFetching = true
        })
        builder.addCase(Authorization.pending, (state) => {
            state.isAuthFetching = true
        })
        builder.addCase(Authorization.rejected, (state, action) => {
            state.isAuthFetching = false
            if (action.payload?.fields) {
                Object.entries(action.payload.fields).forEach((item) => {
                    const [key, values] = item
                    if (values && values.length !== 0) {
                        state.error[key] = values.join(', ')
                    }
                })
            }
        })
        builder.addCase(Authorization.fulfilled, (state, action) => {
            if (action.payload.data) {
                window.location.replace(action.payload.data)
                localStorage.removeItem(LocalStorageEnum.registerToken)
            } else {
                state.isAuthFetching = false
            }
        })
        builder.addCase(AuthorizationByToken.fulfilled, (state, action) => {
            if (action.payload.data) {
                window.location.replace(action.payload.data)
            } else {
                state.isAuthFetching = false
            }
        })
        builder.addCase(AuthorizationByToken.rejected, (state) => {
            state.isAuthFetching = false
            state.token = null
            localStorage.removeItem(LocalStorageEnum.access_token)
        })
        builder.addCase(AuthorizationByToken.pending, (state) => {
            state.isAuthFetching = true
        })

        builder.addCase(SendPhoneCode.fulfilled, (state, action) => {
            localStorage.setItem(LocalStorageEnum.registerToken, action.payload.data.access_token)
            state.phone = action.meta.arg.phone
            if (action.meta.arg.needPhoneVerification === false) {
                state.phoneSubmit = true
                state.registerStep = RegisterStepEnum.PhoneSubmit
                return
            }

            //state.phoneSubmit = false
            // state.retryAfter = action.payload.data.retry_after
            // state.registerStep = RegisterStepEnum.PhoneSent
        })
        builder.addCase(SendPhoneCode.rejected, (state, action) => {
            if (action.payload?.status === APIStatusEnum.TooManyRequests) {
                state.retryAfter = action.payload?.retryData
                state.registerStep = RegisterStepEnum.PhoneSent
                state.phone = action.meta.arg.phone
            }
        })

        builder.addCase(SubmitPhoneCode.fulfilled, (state) => {
            state.phoneSubmit = true
            state.registerStep = RegisterStepEnum.PhoneSubmit
        })
        builder.addCase(RetrySMS.pending, (state) => {
            state.smsPending = true
        })
        builder.addCase(RetrySMS.fulfilled, (state, action) => {
            state.retryAfter = action.payload.data
            state.smsPending = false
        })
        builder.addCase(RetrySMS.rejected, (state, action) => {
            if (action.payload?.status === APIStatusEnum.TooManyRequests) {
                state.retryAfter = action.payload?.retryData
            }
            state.smsPending = false
        })
        builder.addCase(RepeatRecoveryCode.pending, (state) => {
            state.smsPending = true
        })
        builder.addCase(RepeatRecoveryCode.fulfilled, (state, action) => {
            state.retryAfter = action.payload.data
            state.smsPending = false
        })
        builder.addCase(RepeatRecoveryCode.rejected, (state, action) => {
            if (action.payload?.status === APIStatusEnum.TooManyRequests) {
                state.retryAfter = action.payload?.retryData
            }
            state.smsPending = false
        })

        builder.addCase(SendMailCode.fulfilled, (state, action) => {
            localStorage.setItem(LocalStorageEnum.registerToken, action.payload.data.access_token)
            state.mailSubmit = false
            state.mail = action.meta.arg.email
            state.retryAfter = action.payload.data.retry_after
            state.registerStep = RegisterStepEnum.MailSent
        })

        builder.addCase(SendMailCode.rejected, (state, action) => {
            if (action.payload?.status === APIStatusEnum.TooManyRequests) {
                state.retryAfter = action.payload?.retryData
                state.mail = action.meta.arg.email
            }
        })

        builder.addCase(SubmitMailCode.fulfilled, (state) => {
            state.mailSubmit = true
            state.registerStep = RegisterStepEnum.MailSubmit
        })

        builder.addCase(RetryMailCode.fulfilled, (state, action) => {
            state.retryAfter = action.payload.data
            state.codePending = false
        })

        builder.addCase(RetryMailCode.pending, (state) => {
            state.codePending = true
        })
        builder.addCase(RetryMailCode.rejected, (state, action) => {
            if (action.payload?.status === APIStatusEnum.TooManyRequests) {
                state.retryAfter = action.payload?.retryData
            }
            state.codePending = false
        })

        builder.addCase(SetPassword.fulfilled, (state) => {
            state.passwordFilled = true
            resetRegister()
        })

        builder.addCase(Logout.fulfilled, (state) => {
            state.registerStep = RegisterStepEnum.Start
            state.isFetching = false
            state.error = {}
            state.phone = null
            state.mail = null
            state.phoneSubmit = false
            state.mailSubmit = false
            state.retryAfter = null
            state.token = null
            resetRegister()
            window.location.replace('/sign-in')
        })


        builder.addCase(SearchUser.fulfilled, (state, action) => {
                const user = RecoverySearchUser.parse(action.payload.data)
                state.recoveryUserInfo = {
                    ...state.recoveryUserInfo,
                    name: user.name,
                    surname: user.surname,
                    patronymic: user.patronymic,
                    email: user.email,
                    sex_id: user.sex_id,
                    avatar: user.avatar
                }
                state.recoveryData = action.meta.arg
                state.recoveryStep = RecoveryStepEnum.RecoverySendEmail
            }
        )
        builder.addCase(SearchUser.rejected, (state) => {
            state.recoveryStep = RecoveryStepEnum.Recovery
        })
        builder.addCase(ConfirmUser.fulfilled, (state) => {
                state.recoveryStep = RecoveryStepEnum.RecoverySendEmail
            }
        )

        builder.addCase(ConfirmRecoveryCode.fulfilled, (state, action) => {
                state.accounts = action.payload.data.accounts

                if(state.accounts.length > 1){
                    state.recoveryStep = RecoveryStepEnum.RecoveryAccount
                }else{
                    state.recoveryStep = RecoveryStepEnum.CreateNewPassword
                }
            }
        )

        builder.addCase(GetPhone.fulfilled, (state, action) => {
            state.recoveryPhone = action.payload.data
        })

        builder.addCase(PassportCheck.pending, (state) => {
            state.isPassportChecking = true
        })
        builder.addCase(PassportCheck.rejected, (state) => {
            state.isPassportChecking = false
        })
        builder.addCase(PassportCheck.fulfilled, (state) => {
            state.isPassportChecking = false
        })
        builder.addCase(UpdatePassword.fulfilled, () => {
            localStorage.removeItem('selectedUser')
        })
        builder.addCase(RecoverySendCode.fulfilled, (state, action) => {

            const data = RecoverySendCodeData.parse(action.payload.data)
            state.recoveryStep = RecoveryStepEnum.RecoveryPhoneCode
            state.recoveryPhone = action.meta.arg
            state.retryAfter = data.retry_after
            state.recoveryType = data.recovery_type
            state.sentTo = data.sent_to

            localStorage.setItem('recoveryPhone', action.meta.arg)
            localStorage.setItem('recoveryToken', data.token)
            localStorage.setItem('recoveryRetryAfter', data.retry_after.toString())

            const diff = moment().diff(moment(data.expires_in, 'YYYY-MM-DD HH:mm:ss'))
            const check = () => {
                if (diff < 0) {
                    localStorage.removeItem('recoveryPhone')
                    localStorage.removeItem('recoveryToken')
                    localStorage.removeItem('recoveryRetryAfter')
                    window.location.reload()
                }
            }
            setInterval(check, 1000)
        })
        builder.addCase(RecoveryByEmail.fulfilled, (state, action) => {

            const data = RecoverySendCodeData.parse(action.payload.data)
            state.recoveryStep = RecoveryStepEnum.RecoveryPhoneCode
            state.recoveryPhone = action.meta.arg
            state.retryAfter = data.retry_after

            localStorage.setItem('recoveryPhone', action.meta.arg)
            localStorage.setItem('recoveryToken', data.token)
            localStorage.setItem('recoveryRetryAfter', data.retry_after.toString())

            const diff = moment().diff(moment(data.expires_in, 'YYYY-MM-DD HH:mm:ss'))
            const check = () => {
                if (diff < 0) {
                    localStorage.removeItem('recoveryPhone')
                    localStorage.removeItem('recoveryToken')
                    localStorage.removeItem('recoveryRetryAfter')
                    window.location.reload()
                }
            }
            setInterval(check, 1000)
        })

        builder.addCase(CheckConnections.fulfilled, (state, action) => {
            state.connection.data = action.payload.data.connections
            state.connection.token = action.payload.data.access_token
            state.connection.canLogIn = action.payload.data.connections.length === 0
            state.accounts = action.payload.data.accounts
            state.needAuth = action.payload.data.need_auth
            state.loginAdminEmail = action.payload.data.email
            localStorage.removeItem('selectedUser')
            if (state.connection.token == "" && state.needAuth && state.accounts.length != 0) {
                state.unbindingsAccounts = true
            }
        })

        builder.addCase(CheckConnectionsAdmin.fulfilled, (state, action) => {
            state.loginAdminToken = action.payload.data.access_token
            state.loginAdminEmail = action.meta.arg.username
        })

        builder.addCase(ContinueConnectionAdmin.fulfilled, (state) => {
            state.loginAdminSuccess = true
            state.isFetchingInAuth = false
        })
        builder.addCase(ContinueConnectionAdmin.pending, (state) => {
            state.isFetchingInAuth = true
        })
        builder.addCase(ContinueConnectionAdmin.rejected, (state) => {
            state.loginAdminSuccess = true
            state.isFetchingInAuth = false
        })
        builder.addCase(Replace.fulfilled, (state) => {
            state.loginAdminSuccess = true
            state.isFetchingInAuth = false
        })
        builder.addCase(Replace.pending, (state) => {
            state.isFetchingInAuth = true
        })
        builder.addCase(Replace.rejected, (state) => {
            state.isFetchingInAuth = false
        })
        builder.addCase(GetPersons.fulfilled, (state, action) => {
            state.loginAdminPersons = action.payload.data
        })

        builder.addCase(GetPerson.fulfilled, (state, action) => {
            state.loginAdminInitialPerson = action.payload.data[0]
        })

        builder.addCase(BindConnection.fulfilled, (state, action) => {
            state.connection.connectedAccountId = action.meta.arg.data.common_name
            state.connection.accountToConnectId = null
            state.connection.accountSubmitted = true
            state.needAuth = null
        })

        builder.addCase(GetRegistrationInitData.fulfilled, (state, action) => {
            const parsed = RegistrationInitDataModel.parse(action.payload.data)
            if (parsed) {
                state.registerInitData = parsed
            }
            state.registerInitDataFetching = false
        })

        builder.addCase(GetRegistrationInitData.pending, (state) => {
            state.registerInitDataFetching = true
        })
        builder.addCase(GetRegistrationInitData.rejected, (state) => {
            state.registerInitDataFetching = false
        })
        builder.addCase(GetConfirmationRequirements.fulfilled, (state, action) => {
            state.needPhoneVerification = ConfirmationRequirementsModel.parse(action.payload.data).phone_verification
        })
        builder.addMatcher(isAnyOf(
            ContinueConnection.pending,
            BindConnection.pending,
            SkipConnection.pending,
        ), (state) => {
            state.connection.isBinding = true
        })
        builder.addMatcher(isAnyOf(
            ContinueConnection.rejected,
            ContinueConnection.fulfilled,

            BindConnection.rejected,
            BindConnection.fulfilled,

            SkipConnection.rejected,
            SkipConnection.fulfilled,
        ), (state) => {
            state.connection.isBinding = false
        })

        builder.addMatcher(isAnyOf(
            SendPhoneCode.rejected,
            SubmitPhoneCode.rejected,
            SendMailCode.rejected,
            SubmitMailCode.rejected,
            SetPassword.rejected,
            ConfirmRecoveryCode.rejected,
            SearchUser.rejected,
            UpdatePassword.rejected,
            RecoverySendCode.rejected,
            CheckConnections.rejected,
            CheckConnectionsAdmin.rejected,
            GetPersons.rejected,
            GetPerson.rejected,
            BindConnection.rejected,
        ), (state, action) => {
            state.isFetching = false
            if (action.payload?.fields) {
                Object.entries(action.payload.fields).forEach((item) => {
                    const [key, values] = item
                    if (values && values.length !== 0) {
                        state.error[key] = values.join(', ')
                    }

                })
            }
        })
        builder.addMatcher(isAnyOf(
            SendPhoneCode.pending,
            SendMailCode.pending,
            SubmitMailCode.pending,
            SetPassword.pending,
            ConfirmRecoveryCode.pending,
            SearchUser.pending,
            UpdatePassword.pending,
            RecoverySendCode.pending,
            CheckConnections.pending,
            BindConnection.pending,
        ), (state) => {
            state.error = {}
        })
        builder.addMatcher(isAnyOf(
            SendPhoneCode.fulfilled,
            SubmitPhoneCode.fulfilled,
            SendMailCode.fulfilled,
            SubmitMailCode.fulfilled,
            SetPassword.fulfilled,
            RecoverySendCode.fulfilled,
            CheckConnections.fulfilled,
            CheckConnectionsAdmin.fulfilled,
            GetPersons.fulfilled,
            GetPerson.fulfilled,
        ), (state) => {
            state.isFetching = false
        })
        builder.addMatcher(isAnyOf(
            SendPhoneCode.pending,
            SubmitPhoneCode.pending,
            SendMailCode.pending,
            SubmitMailCode.pending,
            SetPassword.pending,
            RecoverySendCode.pending,
            CheckConnections.pending,
            CheckConnectionsAdmin.pending,
            GetPersons.pending,
            GetPerson.pending,
        ), (state) => {
            state.isFetching = true
        })
    }
})
export const {
    cleanError,
    setRecoveryStep,
    setRegisterStep,
    setPhone,
    setMail,
    resetState,
    setPhoneSubmit,
    setMailSubmit,
    setRecoveryData,
    setCaptcha,
    addError,
    setRecoveryPassword,
    // clearRegistered,
    clearCountdown,
    clearCallbackDelay,
    cleanAllErrors,
    setAccountToConnectId,
    resetPasswordFilled,
    setNeedPasswordReset,
    setNeedAuth,
    loginAdminExit,
    setIsTryingAsAdmin,
    forceSetToUserChoose,
    clearAndExit
} = slice.actions;


export default slice.reducer;
